import { useMemo } from 'react';

import { PureSwitch } from '@/components/form/Switch'
import { keyOf, NameAndCreateDateFormatter } from '@Components/Grid/components/Table/formatters';
import column, { defaultColumn, defaultActionsColumn, duplicateAction, editAction, defaultColumnId, extraActions, defaultActiveColumn } from '@Components/Grid/utils/column';

import CostModeFormatter from './CostModeFormatter';
import CostUpdater from './CostUpdater';
import DefaultToggle from './DefaultToggle';
import DeleteEntity from './DeleteEntity';
import Generator from './Generator';
import PayoutFormatter from './PayoutFormatter';
import PublisherLinkFormatter from './PublisherLinkFormatter';
import ThrottlingFormatter from './ThrottlingFormatter';

export default function useColumns() {
  return useMemo(() => [
    defaultColumnId(),
    column.accessor('description', {
      header: 'Description',
      cell: info => <NameAndCreateDateFormatter info={info} />,
      initialSize: 'max-w-[20vw]',
    }),
    defaultColumn('slug', 'Slug', undefined, { initialSize: 'max-w-[20vw]', enableTruncateTooltip: true }),
    column.accessor('publisher_id', {
      header: 'Publisher',
      cell: info => <PublisherLinkFormatter info={info} />,
    }),
    column.accessor('cost_mode', {
      header: 'Cost Mode',
      cell: info => <CostModeFormatter info={info} />,
    }),
    column.accessor('payouttype', {
      header: 'Payout Type',
      cell: info => PAYOUT_TYPE_LABELS[info.getValue()] || info.getValue(),
      defaultVisible: false,
    }),
    column.accessor('payout', {
      header: 'Payout',
      cell: info => <PayoutFormatter info={info} />,
    }),
    column.accessor('mobile_payout', {
      header: 'Mobile Payout',
      cell: info => <PayoutFormatter info={info} />,
      defaultVisible: false,
    }),
    column.accessor('scrub_percent', {
      header: 'Throttling Method',
      cell: info => <ThrottlingFormatter info={info} />,
      enableSorting: false,
      initialSize: 'max-w-[15vw]',
    }),
    column.accessor('skip_dup_reg', {
      header: 'Skip duplicated registration',
      cell: info => <PureSwitch value={!!info.getValue()} disabled />,
      defaultVisible: false,
      enableColumnResize: false,
    }),
    column.accessor('meta.multDomain.enabled', {
      header: 'Multiple Domain',
      cell: info => <PureSwitch value={!!info.getValue()} disabled />,
      defaultVisible: false,
      enableSorting: false,
      enableColumnResize: false,
    }),
    defaultColumn('default', 'Default', defaultToggleFormatter, { enableColumnResize: false }),
    defaultActiveColumn(),
    defaultActionsColumn(editAction, deleteAction,
      extraActions(
        duplicateAction,
        generatorAction,
        costUpdaterAction,
      )),
  ], [])
}

const PAYOUT_TYPE_LABELS = {
  cpc: 'CPC',
  cpa: 'CPA',
  cpm: 'CPM',
  cpl: 'CPL',
  custom: 'Custom',
}

function defaultToggleFormatter(info) {
  return <DefaultToggle key={keyOf(info)} info={info} />
}

function deleteAction(info) {
  return <DeleteEntity key={keyOf(info, 'delete')} info={info} />
}

function generatorAction(info) {
  return <Generator key={keyOf(info, 'generator')} info={info} />
}

function costUpdaterAction(info) {
  return <CostUpdater key={keyOf(info, 'cost-updater')} info={info} />
}
